import React, { useState, useRef, useEffect } from "react"
import { useMap } from "react-leaflet"
import { Paper, IconButton, Tabs, Tab, Box, Typography, Tooltip } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen"
import DragHandleIcon from "@mui/icons-material/DragHandle"
import { onMouseEnter, onMouseLeave } from "@/utils/mapUx"
import { useIsCalculating, useRouteStore } from "@/features/route/state/RouteStore.jsx"
import useMinuteTick from "@/hooks/useMinuteTick"
import { draftDisplayName, selectRouteWeatherLimits } from "@/features/route/state/RouteSelectors.jsx"
import {
  PANEL_VIEWPORT_GAP,
  LEFT_PANEL_WIDTH,
} from "@/config/layout"
import { useLayoutBounds } from "@/features/layout/layoutBounds.jsx"
import useComparisonSummaries from "./useComparisonSummaries.jsx"
import RouteOptionCards from "./RouteOptionCards.jsx"
import { TourHelpButton } from "@/features/tutorials"
import RouteWaypointGrid from "./RouteWaypointGrid.jsx"
import { transitTableItems } from "@/features/route/transits.js"

// Near-white sub-card matching the vessel panel's content cards, so the route
// table reads as part of the same panel family.
const CARD_SX = {
  p: 1.25,
  borderRadius: "14px",
  background: "#fafbfc",
  border: "1px solid rgba(0, 79, 125, 0.10)",
}

const TAB_STYLE = {
  active: { color: "#2e7d32", bg: "rgba(46, 125, 50, 0.10)" },
  suggested: { color: "#7b1fa2", bg: "rgba(123, 31, 162, 0.10)" },
  archived: { color: "#6b7480", bg: "rgba(107, 116, 128, 0.12)" },
  unsaved: { color: "#b7791f", bg: "rgba(245, 158, 11, 0.15)" },
}

function tabSx(style) {
  return {
    textTransform: "none",
    color: style.color,
    px: 1.5,
    py: 0.5,
    mr: 0.5,
    borderRadius: "999px",
    "&.Mui-selected": {
      color: style.color,
      backgroundColor: style.bg,
      fontWeight: 600,
    },
  }
}

export default function TablePanel({
  vesselPlanRoutes,
  selectedDraftRoutes,
  activeRouteId,
  routeSummariesById,
  routeTimestampsById,
  setVoyageActive,
  vesselPanelOpen,
  otherPanelOpen,
  openRouteTable,
  setOpenRouteTable,
  routeConsumption,
}) {
  // Staleness badges are derived at render time; tick so they flip on their own.
  useMinuteTick()
  const { draftTabs, comparisonSummaries } = useComparisonSummaries({
    selectedDraftRoutes,
    vesselPlanRoutes,
    activeRouteId,
    routeSummariesById,
    routeTimestampsById,
    routeConsumption,
  })
  const draftTabCount = draftTabs.length

  const map = useMap()
  const { state: routeState } = useRouteStore()

  // The limits the route was computed with; vessel/system default for routes without.
  const gridLimits = (route) => selectRouteWeatherLimits(routeState, route)
  const isCalculating = useIsCalculating()

  const bounds = useLayoutBounds()

  const computeDockLeft = (expanded = isExpanded) => {
    if (expanded || !vesselPanelOpen) return bounds.left
    if (bounds.leftColumnRight != null) return bounds.leftColumnRight + PANEL_VIEWPORT_GAP
    return bounds.left + LEFT_PANEL_WIDTH + PANEL_VIEWPORT_GAP
  }

  const computeMaxWidth = (expanded = isExpanded) => {
    const available = bounds.right - computeDockLeft(expanded)
    return Math.max(280, available)
  }

  const [paperWidth, setPaperWidth] = useState(() => computeMaxWidth(false))
  const [widthRatio, setWidthRatio] = useState(1)
  const [isExpanded, setIsExpanded] = useState(false)
  // null = fit the summary content until the user drags the handle.
  const [heightRatio, setHeightRatio] = useState(null)
  const summaryScrollRef = useRef(null)
  const [dockTop, setDockTop] = useState(bounds.top)
  const [dockHeight, setDockHeight] = useState(null)
  const [dockLeft, setDockLeft] = useState(() => computeDockLeft())

  const toggleExpand = () => {
    setIsExpanded((prev) => {
      const next = !prev
      setPaperWidth(computeMaxWidth(next))
      setWidthRatio(1)
      return next
    })
  }

  const [selectedTab, setSelectedTab] = useState(0)
  const [viewTab, setViewTab] = useState("compare")

  useEffect(() => {
    const totalTabs = draftTabCount + vesselPlanRoutes.length
    if (totalTabs === 0) {
      if (selectedTab !== 0) setSelectedTab(0)
      return
    }
    setSelectedTab((prev) => Math.max(0, Math.min(prev, totalTabs - 1)))
  }, [vesselPlanRoutes.length, draftTabCount, selectedTab])

  const paperRef = useRef()
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    if (openRouteTable) setIsOpen(true)
  }, [openRouteTable])

  const handleVerticalDragStart = (e) => {
    e.preventDefault()
    e.stopPropagation()
    map?.dragging?.disable()
    setIsExpanded(false)

    const panelTop = bounds.top
    const bottomLimit = dockTop + (dockHeight ?? 0)
    const fullHeight = Math.max(1, bottomLimit - panelTop)

    const handleMove = (ev) => {
      const minHeight = Math.min(160, fullHeight)
      const top = Math.max(panelTop, Math.min(bottomLimit - minHeight, ev.clientY))
      setHeightRatio((bottomLimit - top) / fullHeight)
    }
    const handleUp = () => {
      document.removeEventListener("mousemove", handleMove)
      document.removeEventListener("mouseup", handleUp)
      map?.dragging?.enable()
    }
    document.addEventListener("mousemove", handleMove)
    document.addEventListener("mouseup", handleUp)
  }

  useEffect(() => {
    const recomputeGeometry = () => {
      const maxWidth = computeMaxWidth()
      setPaperWidth(Math.min(maxWidth, Math.max(280, maxWidth * widthRatio)))
      setDockLeft(computeDockLeft())
      const panelTop = bounds.top
      const bottomLimit = bounds.bottom
      const fullHeight = Math.max(0, bottomLimit - panelTop)
      const minHeight = Math.min(160, fullHeight)
      const scrollEl = summaryScrollRef.current
      const contentEl = scrollEl?.firstElementChild
      const rectHeight = (el) => el.getBoundingClientRect().height
      const fitHeight =
        contentEl && paperRef.current
          ? rectHeight(paperRef.current) - rectHeight(scrollEl) + rectHeight(contentEl)
          : (dockHeight ?? fullHeight * 0.33)
      const wanted = heightRatio == null ? fitHeight : fullHeight * heightRatio
      const height = isExpanded ? fullHeight : Math.max(minHeight, Math.min(fullHeight, wanted))
      setDockHeight(height)
      setDockTop(bottomLimit - height)
    }

    recomputeGeometry()
  }, [bounds, widthRatio, heightRatio, openRouteTable, isExpanded, vesselPanelOpen, comparisonSummaries, viewTab, paperWidth])

  const handleClosePaper = () => {
    onMouseLeave(map)
    setIsOpen(false)
    setVoyageActive(true)
    setOpenRouteTable(false)
  }

  const hasRoutes = vesselPlanRoutes.length > 0 || draftTabCount > 0
  if (!(hasRoutes && openRouteTable && isOpen && !otherPanelOpen)) return null

  const selectedDraft = selectedTab < draftTabCount ? draftTabs[selectedTab] : null
  const selectedRoute = selectedDraft ? null : vesselPlanRoutes[selectedTab - draftTabCount]
  const gridRoute = selectedDraft ?? selectedRoute
  const gridTransits =
    gridRoute && !gridRoute.sketch ? transitTableItems(gridRoute.summary, gridRoute.points?.geojson?.features) : []

  return (
    <Paper
      id="route-table"
      data-tour="table-panel"
      ref={paperRef}
      elevation={0}
      style={{
        position: "absolute",
        top: `${dockTop}px`,
        left: `${dockLeft}px`,
        height: `${dockHeight ?? bounds.panelHeight}px`,
        zIndex: 1150,
        overflow: "hidden",
        backgroundColor: "#fff",
        borderRadius: "0px",
        width: `${paperWidth}px`,
        display: "flex",
        flexDirection: "column",
        overscrollBehavior: "contain",
      }}
      onMouseEnter={() => {
        onMouseEnter(map)
        setVoyageActive(false)
      }}
      onMouseLeave={() => {
        onMouseLeave(map)
        setVoyageActive(true)
      }}
    >
      <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 2, px: 2, pt: 1.5, pb: 1 }}>
        <Box
          onMouseDown={handleVerticalDragStart}
          sx={{
            position: "absolute",
            left: "50%",
            top: "7px",
            transform: "translate(-50%, -50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "ns-resize",
            color: "rgba(0, 79, 125, 0.4)",
            "&:hover": { color: "#004f7d" },
          }}
        >
          <DragHandleIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box
          data-tour="table-view-switch"
          sx={{
            display: "inline-flex",
            p: 0.25,
            borderRadius: "12px",
            backgroundColor: "rgba(0, 79, 125, 0.06)",
            border: "1px solid rgba(0, 79, 125, 0.08)",
          }}
        >
          {[
            { value: "compare", label: "Summary" },
            { value: "waypoints", label: "Waypoints" },
          ].map((opt) => {
            const selected = viewTab === opt.value
            return (
              <Box
                key={opt.value}
                onClick={() => setViewTab(opt.value)}
                sx={{
                  px: 2,
                  py: 0.5,
                  fontSize: 13,
                  fontWeight: selected ? 600 : 500,
                  color: selected ? "#fff" : "#004f7d",
                  backgroundColor: selected ? "#004f7d" : "transparent",
                  borderRadius: "9px",
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: selected ? "#004f7d" : "rgba(0, 79, 125, 0.08)",
                  },
                }}
              >
                {opt.label}
              </Box>
            )
          })}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <TourHelpButton tourId="compare-routes" />
        <Tooltip title={isExpanded ? "Collapse" : "Expand"} arrow>
          <IconButton
            onClick={toggleExpand}
            size="small"
            sx={{ color: "#004f7d", "&:hover": { backgroundColor: "rgba(0, 79, 125, 0.08)" } }}
          >
            {isExpanded ? <CloseFullscreenIcon sx={{ fontSize: 18 }} /> : <OpenInFullIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Close" arrow>
          <IconButton
            onClick={handleClosePaper}
            size="small"
            sx={{ color: "#004f7d", "&:hover": { backgroundColor: "rgba(0, 79, 125, 0.08)" } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {viewTab === "waypoints" && (
        <Box sx={{ display: "flex", alignItems: "center", py: 1.5, px: 1 }}>
          <Tabs
            value={selectedTab}
            onChange={(event, value) => setSelectedTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flexGrow: 1,
              minHeight: 36,
              "& .MuiTab-root": { minHeight: 36, px: 2, py: 0.5, display: "flex", alignItems: "center" },
            }}
          >
            {draftTabs.map((draft) => (
              <Tab
                key={`draft-${draft?.points?.routeId ?? "na"}`}
                sx={tabSx(TAB_STYLE.unsaved)}
                label={
                  <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.1, alignItems: "flex-start" }}>
                    <Typography sx={{ color: "inherit", fontWeight: 600, fontSize: 13 }}>
                      {draftDisplayName(draft, draftTabs)}
                    </Typography>
                    {draftTabCount > 1 && (
                      <Typography sx={{ color: "inherit", fontSize: 11 }}>
                        {draft?.points?.vesselName ?? ""}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
            {vesselPlanRoutes.map((route, index) => (
              <Tab
                key={route?.points?.routeId ?? index}
                sx={tabSx(TAB_STYLE[route?.points?.state] ?? TAB_STYLE.active)}
                label={
                  <Typography sx={{ color: "inherit", fontWeight: 600, fontSize: 13 }}>
                    {route.points.name || `Route ${route.points.routeId}`}
                  </Typography>
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      {viewTab === "compare" ? (
        <Box sx={{ pt: 0.5, pb: 1.5, px: 1.5, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {comparisonSummaries.length === 0 ? (
            <Box sx={{ ...CARD_SX, boxSizing: "border-box", width: "100%" }}>
              <Typography sx={{ fontSize: "12px", color: "#57728d", py: 0.5 }}>
                Select routes in the Vessel panel to compare them.
              </Typography>
            </Box>
          ) : (
            <RouteOptionCards
              scrollRef={summaryScrollRef}
              summaries={comparisonSummaries}
              isCalculating={isCalculating}
            />
          )}
        </Box>
      ) : selectedDraft ? (
        <RouteWaypointGrid
          key={`draft-${selectedDraft?.points?.routeId ?? "na"}`}
          voyagePoints={selectedDraft.points.geojson}
          {...gridLimits(selectedDraft)}
          transits={gridTransits}
        />
      ) : selectedRoute ? (
        <RouteWaypointGrid
          key={`route-${selectedRoute?.points?.routeId ?? selectedTab}`}
          voyagePoints={selectedRoute.points.geojson}
          {...gridLimits(selectedRoute)}
          transits={gridTransits}
        />
      ) : null}
    </Paper>
  )
}
